import React, { useEffect, useRef, useState } from 'react';
import { useAppStore } from '../App';
import { setUserLocale, setUserTimezone, formatDateTime } from '../utils/datetime';
import { renderMarkdown } from '../utils/markdown';

/**
 * 프로필 + 표시 환경 통합 설정 페이지.
 *
 * 좌측 메뉴 '설정' 진입점. 옛 프로필 편집 모달을 흡수.
 *
 * 섹션:
 *  1. 프로필 이미지
 *  2. 계정 정보 (이메일/hash) — 읽기 전용
 *  3. 프로필 편집 (이름/전화/사업자번호) — PUT /auth/profile
 *  4. 표시 환경 (언어/시간대) — PATCH /auth/preferences
 */

const LOCALE_OPTIONS: Array<{ value: string; label: string }> = [
 { value: '', label: '자동 감지' },
 { value: 'ko-KR', label: '한국어 (대한민국)' },
 { value: 'en-US', label: 'English (United States)' },
 { value: 'en-GB', label: 'English (United Kingdom)' },
 { value: 'ja-JP', label: '日本語 (日本)' },
 { value: 'zh-CN', label: '中文 (简体, 中国)' },
 { value: 'zh-TW', label: '中文 (繁體, 台灣)' },
];

const TIMEZONE_OPTIONS: Array<{ value: string; label: string }> = [
 { value: '', label: '자동 감지' },
 { value: 'Asia/Seoul', label: '한국 표준시 (서울)' },
 { value: 'Asia/Tokyo', label: '일본 표준시 (도쿄)' },
 { value: 'Asia/Shanghai', label: '중국 표준시 (상하이)' },
 { value: 'America/New_York', label: '미국 동부 (뉴욕)' },
 { value: 'America/Los_Angeles', label: '미국 서부 (LA)' },
 { value: 'Europe/London', label: '영국 (런던)' },
 { value: 'Europe/Paris', label: '중부유럽 (파리)' },
 { value: 'Australia/Sydney', label: '호주 (시드니)' },
 { value: 'UTC', label: 'UTC' },
];

const ProfilePage: React.FC = () => {
 const user = useAppStore(s => s.user);
 const setUser = useAppStore(s => s.setUser);

 // 프로필 폼 (이름/전화/사업자번호)
 const [profileForm, setProfileForm] = useState({
  name: user?.name || '',
  phone: user?.phone || '',
  businessRegistrationNumber: user?.businessRegistrationNumber || '',
 });
 const [profileImagePreview, setProfileImagePreview] = useState<string | null>(null);
 const fileInputRef = useRef<HTMLInputElement>(null);
 const [profileSaving, setProfileSaving] = useState(false);
 const [profileSaved, setProfileSaved] = useState(false);
 const [profileError, setProfileError] = useState<string | null>(null);

 // 비로그인 업로드(@handle) 설정
 const [uploadCfg, setUploadCfg] = useState<{ handle: string; uploadCode: string; effectiveCode: string; isCustomCode: boolean; profileBackground: string; uploadPageNotice: string; instagramUsername: string; publicUrl: string | null }>(
  { handle: '', uploadCode: '', effectiveCode: '', isCustomCode: false, profileBackground: '', uploadPageNotice: '', instagramUsername: '', publicUrl: null }
 );
 const NOTICE_MAX = 4000; // 서버 auth.ts NOTICE_MAX 와 같은 값
 const [noticeImgUploading, setNoticeImgUploading] = useState(false);
 const [noticePreview, setNoticePreview] = useState(false);
 const noticeRef = useRef<HTMLTextAreaElement | null>(null);
 const [uploadCfgSaving, setUploadCfgSaving] = useState(false);
 const [uploadCfgSaved, setUploadCfgSaved] = useState(false);
 const [uploadCfgError, setUploadCfgError] = useState<string | null>(null);
 const [bgUploading, setBgUploading] = useState(false);

 // 배경 이미지 파일 업로드 — 즉시 서버 저장(profile_background 갱신) 후 미리보기 반영.
 const handleBgFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
  const file = e.target.files?.[0];
  if (!file) return;
  setBgUploading(true);
  setUploadCfgError(null);
  try {
   const fd = new FormData();
   fd.append('background', file);
   const r = await fetch('/auth/profile-background', { method: 'POST', credentials: 'include', body: fd });
   const j = await r.json();
   if (r.ok && j?.success) setUploadCfg(c => ({ ...c, profileBackground: j.data.profileBackground }));
   else setUploadCfgError(j?.error || '배경 업로드에 실패했습니다.');
  } catch {
   setUploadCfgError('네트워크 오류입니다.');
  } finally {
   setBgUploading(false);
   e.target.value = '';
  }
 };

 // 안내문 이미지 업로드 — 서버에 저장한 뒤 `![설명](url)` 을 textarea 커서 위치에 끼워 넣는다.
 // 본문 저장은 별도("업로드 설정 저장") — 저장 시 본문에 없는 이미지는 하루 뒤 서버가 정리한다.
 const handleNoticeImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
  const file = e.target.files?.[0];
  if (!file) return;
  setNoticeImgUploading(true);
  setUploadCfgError(null);
  try {
   const fd = new FormData();
   fd.append('image', file);
   const r = await fetch('/auth/notice-image', { method: 'POST', credentials: 'include', body: fd });
   const j = await r.json();
   if (!(r.ok && j?.success)) { setUploadCfgError(j?.error || '이미지 업로드에 실패했습니다.'); return; }
   const snippet: string = j.data.markdown;
   const ta = noticeRef.current;
   setUploadCfg(c => {
    const cur = c.uploadPageNotice;
    const pos = ta ? ta.selectionStart : cur.length;
    const before = cur.slice(0, pos);
    const after = cur.slice(pos);
    // 이미지는 블록으로 — 앞뒤에 줄바꿈을 보장해 한 줄짜리 이미지 블록 규칙(renderMarkdown)을 탄다.
    const lead = before.length && !before.endsWith('\n') ? '\n' : '';
    const tail = after.length && !after.startsWith('\n') ? '\n' : '';
    const next = `${before}${lead}${snippet}${tail}${after}`;
    if (next.length > NOTICE_MAX) { setUploadCfgError(`안내문 길이 상한(${NOTICE_MAX}자)을 넘어 이미지를 넣지 못했습니다.`); return c; }
    const caret = before.length + lead.length + snippet.length;
    requestAnimationFrame(() => { if (ta) { ta.focus(); ta.setSelectionRange(caret, caret); } });
    return { ...c, uploadPageNotice: next };
   });
  } catch {
   setUploadCfgError('네트워크 오류입니다.');
  } finally {
   setNoticeImgUploading(false);
   e.target.value = '';
  }
 };

 useEffect(() => {
  fetch('/auth/upload-settings', { credentials: 'include' })
   .then(r => r.json())
   .then(j => {
    if (!j?.success) return;
    const d = j.data;
    setUploadCfg({
     handle: d.handle ?? '',
     uploadCode: '', // 커스텀 코드는 입력창을 비워 둠(플레이스홀더로 안내). 저장 시에만 전송.
     effectiveCode: d.effectiveCode ?? '',
     isCustomCode: !!d.isCustomCode,
     profileBackground: d.profileBackground ?? '',
     uploadPageNotice: d.uploadPageNotice ?? '',
     instagramUsername: d.instagramUsername ?? '',
     publicUrl: d.publicUrl ?? null,
    });
   })
   .catch(() => { /* 조회 실패는 치명 아님 */ });
 }, []);

 const handleUploadCfgSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setUploadCfgSaving(true);
  setUploadCfgSaved(false);
  setUploadCfgError(null);
  try {
   // handle/배경은 항상 전송(빈 문자열=초기화 의도). uploadCode 는 입력했을 때만 전송
   // (빈칸이면 기존 유지 — 실수로 코드가 리셋되지 않게).
   const body: Record<string, string> = {
    handle: uploadCfg.handle.trim(),
    profileBackground: uploadCfg.profileBackground.trim(),
    uploadPageNotice: uploadCfg.uploadPageNotice,
    instagramUsername: uploadCfg.instagramUsername.trim(),
   };
   if (uploadCfg.uploadCode.trim()) body.uploadCode = uploadCfg.uploadCode.trim();
   const r = await fetch('/auth/upload-settings', {
    method: 'PUT', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
   });
   const j = await r.json();
   if (r.ok && j?.success) {
    const d = j.data;
    setUploadCfg({
     handle: d.handle ?? '', uploadCode: '', effectiveCode: d.effectiveCode ?? '',
     isCustomCode: !!d.isCustomCode, profileBackground: d.profileBackground ?? '',
     uploadPageNotice: d.uploadPageNotice ?? '', instagramUsername: d.instagramUsername ?? '',
     publicUrl: d.publicUrl ?? null,
    });
    setUploadCfgSaved(true);
   } else {
    setUploadCfgError(j?.error || '저장에 실패했습니다.');
   }
  } catch {
   setUploadCfgError('네트워크 오류입니다.');
  } finally {
   setUploadCfgSaving(false);
  }
 };

 // 표시 환경 (locale/timezone)
 const [locale, setLocale] = useState<string>(user?.locale ?? '');
 const [timezone, setTimezone] = useState<string>(user?.timezone ?? '');
 const [prefSaving, setPrefSaving] = useState(false);
 const [prefSaved, setPrefSaved] = useState(false);

 // 관리자 진입 이력
 const [sudoHistory, setSudoHistory] = useState<Array<{ actor_hash: string; actor_name: string | null; action: string; created_at: string }>>([]);
 const [sudoLoading, setSudoLoading] = useState(false);

 // 관리자 접근 차단 스위치
 const [adminBlocked, setAdminBlocked] = useState<boolean>(user?.adminAccessBlocked ?? false);
 const [blockSaving, setBlockSaving] = useState(false);

 const handleToggleAdminAccess = async () => {
  const next = !adminBlocked;
  setBlockSaving(true);
  try {
   const r = await fetch('/auth/admin-access', {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ blocked: next }),
   });
   const j = await r.json();
   if (!j.success) throw new Error(j.error || '저장 실패');
   setAdminBlocked(next);
   if (user) setUser({ ...user, adminAccessBlocked: next });
  } catch (e: any) {
   alert(e.message || '저장 실패');
  } finally {
   setBlockSaving(false);
  }
 };

 // 스텝(마스커레이드)에게 내 사적 채팅(DM) 공유 여부 — 기본 공유(on).
 const [shareDirect, setShareDirect] = useState<boolean>(user?.shareDirectWithStaff !== false);
 const [shareSaving, setShareSaving] = useState(false);
 const handleToggleShareDirect = async () => {
  const next = !shareDirect;
  setShareSaving(true);
  try {
   const r = await fetch('/mopeer/share-direct', {
    method: 'PUT', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ share: next }),
   });
   const j = await r.json();
   if (!j.success) throw new Error(j.error || '저장 실패');
   setShareDirect(next);
   if (user) setUser({ ...user, shareDirectWithStaff: next });
  } catch (e: any) {
   alert(e.message || '저장 실패');
  } finally {
   setShareSaving(false);
  }
 };

 // 우리 매장 스텝(마스커레이드) — 내 계정으로 로그인할 스텝 이메일 관리.
 const [staff, setStaff] = useState<Array<{ id: number; aliasEmail: string; pinned: boolean }>>([]);
 const [staffEmail, setStaffEmail] = useState('');
 const [staffBusy, setStaffBusy] = useState(false);
 const [staffErr, setStaffErr] = useState<string | null>(null);
 const loadStaff = async () => {
  try {
   const r = await fetch('/mopeer/staff', { credentials: 'include' });
   const j = await r.json();
   if (j.success) setStaff(j.data ?? []);
  } catch { /* 무시 */ }
 };
 useEffect(() => { if (!user?.isAdmin) void loadStaff(); }, [user?.isAdmin]);
 const addStaff = async () => {
  if (!staffEmail.trim()) return;
  setStaffBusy(true); setStaffErr(null);
  try {
   const r = await fetch('/mopeer/staff', {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: staffEmail.trim() }),
   });
   const j = await r.json();
   if (j.success) { setStaffEmail(''); await loadStaff(); }
   else setStaffErr(j.error || '등록 실패');
  } catch { setStaffErr('등록 실패'); } finally { setStaffBusy(false); }
 };
 const removeStaff = async (id: number) => {
  try {
   const r = await fetch(`/mopeer/staff/${id}`, { method: 'DELETE', credentials: 'include' });
   if ((await r.json()).success) await loadStaff();
  } catch { /* 무시 */ }
 };

 // 회원 탈퇴 (danger zone)
 const [withdrawOpen, setWithdrawOpen] = useState(false);
 const [withdrawConfirm, setWithdrawConfirm] = useState('');
 const [withdrawing, setWithdrawing] = useState(false);

 const handleWithdraw = async () => {
  if (withdrawConfirm.trim() !== '탈퇴') return;
  if (!confirm('정말로 회원 탈퇴를 진행하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) return;
  setWithdrawing(true);
  try {
   const r = await fetch('/auth/withdraw', { method: 'POST', credentials: 'include' });
   const j = await r.json();
   if (!j.success) throw new Error(j.error || '탈퇴 처리에 실패했습니다.');
   alert('회원 탈퇴가 완료되었습니다. 그동안 이용해 주셔서 감사합니다.');
   window.location.href = '/login';
  } catch (e: any) {
   alert(e.message || '탈퇴 처리 중 오류가 발생했습니다.');
   setWithdrawing(false);
  }
 };

 useEffect(() => {
  setSudoLoading(true);
  fetch('/admin/sudo-history/me', { credentials: 'include' })
   .then(r => r.json())
   .then(j => {
    if (j.success) setSudoHistory(j.data || []);
   })
   .catch(() => {})
   .finally(() => setSudoLoading(false));
 }, []);
 const [prefError, setPrefError] = useState<string | null>(null);

 // user 변경 시 폼 동기화
 useEffect(() => {
  if (user) {
   setProfileForm({
    name: user.name || '',
    phone: user.phone || '',
    businessRegistrationNumber: user.businessRegistrationNumber || '',
   });
   setLocale(user.locale ?? '');
   setTimezone(user.timezone ?? '');
   setAdminBlocked(user.adminAccessBlocked ?? false);
  }
 }, [user]);

 const handleProfileSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setProfileSaving(true);
  setProfileError(null);
  setProfileSaved(false);
  try {
   const r = await fetch('/auth/profile', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(profileForm),
   });
   const j = await r.json();
   if (!j.success) throw new Error(j.error || '저장 실패');
   await useAppStore.getState().checkAuth();
   setProfileSaved(true);
  } catch (e: any) {
   setProfileError(e.message || '저장 실패');
  } finally {
   setProfileSaving(false);
  }
 };

 const [imageUploading, setImageUploading] = useState(false);

 const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
  const file = e.target.files?.[0];
  if (!file) return;
  if (file.size > 5 * 1024 * 1024) {
   alert('이미지 크기는 5MB 이하여야 합니다.');
   e.target.value = '';
   return;
  }
  // 낙관적 미리보기(로컬) — 서버 저장 성공 시 실제 URL 로 교체.
  const reader = new FileReader();
  reader.onload = (ev) => setProfileImagePreview(ev.target?.result as string);
  reader.readAsDataURL(file);

  setImageUploading(true);
  try {
   const fd = new FormData();
   fd.append('image', file);
   const r = await fetch('/auth/profile-image', { method: 'POST', credentials: 'include', body: fd });
   const j = await r.json();
   if (r.ok && j?.success) {
    setProfileImagePreview(null);
    if (user) setUser({ ...user, profilePicture: j.data.profilePicture });
   } else {
    setProfileImagePreview(null);
    alert(j?.error || '이미지 업로드에 실패했습니다.');
   }
  } catch {
   setProfileImagePreview(null);
   alert('이미지 업로드 중 오류가 발생했습니다.');
  } finally {
   setImageUploading(false);
   e.target.value = '';
  }
 };

 const handlePreferencesSave = async () => {
  setPrefSaving(true);
  setPrefError(null);
  setPrefSaved(false);
  try {
   const r = await fetch('/auth/preferences', {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
     locale: locale || null,
     timezone: timezone || null,
    }),
   });
   const j = await r.json();
   if (!j.success) throw new Error(j.error || '저장 실패');
   setUserLocale(locale || null);
   setUserTimezone(timezone || null);
   if (user) setUser({ ...user, locale: locale || null, timezone: timezone || null });
   setPrefSaved(true);
  } catch (e: any) {
   setPrefError(e.message || '저장 실패');
  } finally {
   setPrefSaving(false);
  }
 };

 if (!user) return <div className="text-gray-500">로그인 정보 없음</div>;

 return (
  <div className="max-w-2xl">
   <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">설정</h1>
   <p className="text-sm text-gray-500 mb-6">프로필과 표시 환경을 관리합니다.</p>

   {/* 1. 프로필 이미지 */}
   <section className="border border-gray-200 p-4 mb-6">
    <h2 className="text-lg font-semibold mb-3 text-gray-800">프로필 이미지</h2>
    <div className="flex items-center gap-4">
     <div className="relative">
      {profileImagePreview || user.profilePicture ? (
       <img
        src={profileImagePreview || user.profilePicture}
        alt="Profile"
        className="w-24 h-24 rounded-full border-2 border-gray-300 object-cover"
       />
      ) : (
       <div className="w-24 h-24 rounded-full bg-gray-400 flex items-center justify-center text-white text-3xl font-medium">
        {(user.name || user.email || 'U').charAt(0).toUpperCase()}
       </div>
      )}
      <button
       onClick={() => fileInputRef.current?.click()}
       className="absolute bottom-0 right-0 bg-blue-500 text-white p-1.5 hover:bg-blue-600"
       title="이미지 변경"
      >
       <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
       </svg>
      </button>
      <input
       ref={fileInputRef}
       type="file"
       accept="image/*"
       onChange={handleImageUpload}
       className="hidden"
      />
     </div>
     <div className="text-xs text-gray-500">{imageUploading ? '업로드 중…' : '사진 클릭하여 변경 (5MB 이하)'}</div>
    </div>
   </section>

   {/* 2. 계정 정보 (읽기 전용) */}
   <section className="border border-gray-200 p-4 mb-6">
    <h2 className="text-lg font-semibold mb-3 text-gray-800">계정</h2>
    <dl className="space-y-2 text-sm">
     <div className="flex"><dt className="w-32 text-gray-500 flex-shrink-0">이메일</dt><dd>{user.email}</dd></div>
     <div className="flex"><dt className="w-32 text-gray-500 flex-shrink-0">moPeerHash</dt><dd className="text-xs break-all text-gray-600 font-mono">{user.moPeerHash}</dd></div>
    </dl>
   </section>

   {/* 3. 프로필 편집 */}
   <section className="border border-gray-200 p-4 mb-6">
    <h2 className="text-lg font-semibold mb-3 text-gray-800">프로필 편집</h2>
    <form onSubmit={handleProfileSubmit} className="space-y-3">
     <div>
      <label className="block text-xs text-gray-600 mb-1">이름</label>
      <input
       type="text"
       value={profileForm.name}
       onChange={e => setProfileForm({ ...profileForm, name: e.target.value })}
       disabled={profileSaving}
       className="w-full px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
      />
     </div>
     <div>
      <label className="block text-xs text-gray-600 mb-1">전화번호</label>
      <input
       type="tel"
       value={profileForm.phone}
       onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })}
       placeholder="010-0000-0000"
       disabled={profileSaving}
       className="w-full px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
      />
     </div>
     <div>
      <label className="block text-xs text-gray-600 mb-1">사업자 등록번호</label>
      <input
       type="text"
       value={profileForm.businessRegistrationNumber}
       onChange={e => setProfileForm({ ...profileForm, businessRegistrationNumber: e.target.value })}
       placeholder="000-00-00000"
       disabled={profileSaving}
       className="w-full px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
      />
     </div>
     <div className="flex items-center gap-3 pt-2">
      <button
       type="submit"
       disabled={profileSaving}
       className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm disabled:opacity-50"
      >{profileSaving ? '저장 중...' : '프로필 저장'}</button>
      {profileSaved && <span className="text-xs text-green-600">저장됨</span>}
      {profileError && <span className="text-xs text-red-600">{profileError}</span>}
     </div>
    </form>
   </section>

   {/* 비로그인 업로드 페이지 (@handle) */}
   <section className="border border-gray-200 p-4 mb-6">
    <h2 className="text-lg font-semibold mb-1 text-gray-800">비로그인 업로드 페이지</h2>
    <p className="text-xs text-gray-500 mb-4">
     외부인이 로그인 없이 이 주소로 영상을 올릴 수 있습니다. 업로드 코드를 알려 주면 됩니다.
     올라온 영상은 대시보드 "업로드 승인 대기"에서 승인해야 노출됩니다.
    </p>
    <form onSubmit={handleUploadCfgSubmit} className="space-y-3">
     <div>
      <label className="block text-xs text-gray-600 mb-1">handle (공개 주소)</label>
      <div className="flex items-center gap-1">
       <span className="text-sm text-gray-400">/@</span>
       <input
        type="text"
        value={uploadCfg.handle}
        onChange={e => setUploadCfg({ ...uploadCfg, handle: e.target.value })}
        placeholder="mycafe"
        disabled={uploadCfgSaving}
        className="flex-1 px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
       />
      </div>
      <p className="text-[11px] text-gray-400 mt-1">공백 없이 영문/숫자/._- 만. 비우면 페이지가 비공개됩니다.</p>
      {uploadCfg.publicUrl && (
       <a href={uploadCfg.publicUrl} target="_blank" rel="noreferrer" className="text-[11px] text-blue-500 hover:underline">
        {uploadCfg.publicUrl} 열기 ↗
       </a>
      )}
     </div>
     <div>
      <label className="block text-xs text-gray-600 mb-1">업로드 코드</label>
      <input
       type="text"
       value={uploadCfg.uploadCode}
       onChange={e => setUploadCfg({ ...uploadCfg, uploadCode: e.target.value })}
       placeholder={uploadCfg.isCustomCode ? '••••• (변경하려면 새 코드 입력)' : `기본값: ${uploadCfg.effectiveCode}`}
       disabled={uploadCfgSaving}
       className="w-full px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
      />
      <p className="text-[11px] text-gray-400 mt-1">
       현재 유효 코드: <span className="font-mono text-gray-600">{uploadCfg.effectiveCode || '(이름 필요)'}</span>
       {' · '}비우면 기본값(이름 대문자)로 돌아갑니다.
      </p>
     </div>
     <div>
      <label className="block text-xs text-gray-600 mb-1">인스타그램 아이디 (선택)</label>
      <div className="flex items-center gap-1">
       <span className="text-sm text-gray-400">@</span>
       <input
        type="text"
        value={uploadCfg.instagramUsername}
        onChange={e => setUploadCfg({ ...uploadCfg, instagramUsername: e.target.value })}
        placeholder="my_venue"
        disabled={uploadCfgSaving}
        className="flex-1 px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
       />
      </div>
      <p className="text-[11px] text-gray-400 mt-1">
       등록하면 브라우저 플러그인이 이 인스타 계정으로 로그인된 사용자에게 내 페이지를 자동 선택해 줍니다. 비우면 해제됩니다.
      </p>
     </div>
     <div>
      <label className="block text-xs text-gray-600 mb-1">배경 이미지 (선택)</label>
      {uploadCfg.profileBackground && (
       <img src={uploadCfg.profileBackground} alt="배경 미리보기" className="w-full h-24 object-cover border border-gray-200 mb-2" />
      )}
      <div className="flex items-center gap-2 mb-2">
       <input type="file" accept="image/*" onChange={handleBgFile} disabled={bgUploading || uploadCfgSaving} className="text-xs" />
       {bgUploading && <span className="text-xs text-gray-400">업로드 중…</span>}
       {uploadCfg.profileBackground && (
        <button type="button" onClick={() => setUploadCfg({ ...uploadCfg, profileBackground: '' })}
         className="text-xs text-gray-400 hover:text-red-500">제거</button>
       )}
      </div>
      <input
       type="text"
       value={uploadCfg.profileBackground}
       onChange={e => setUploadCfg({ ...uploadCfg, profileBackground: e.target.value })}
       placeholder="또는 이미지 URL 직접 입력"
       disabled={uploadCfgSaving}
       className="w-full px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
      />
     </div>
     <div>
      <div className="flex items-center justify-between mb-1">
       <label className="block text-xs text-gray-600">안내문 (마크다운, 선택)</label>
       <div className="flex items-center gap-3 text-xs">
        <label className={`cursor-pointer text-blue-600 hover:underline ${(noticeImgUploading || uploadCfgSaving) ? 'opacity-50 pointer-events-none' : ''}`}>
         {noticeImgUploading ? '올리는 중…' : '이미지 올리기'}
         <input type="file" accept="image/*" onChange={handleNoticeImage} disabled={noticeImgUploading || uploadCfgSaving} className="hidden" />
        </label>
        <button type="button" onClick={() => setNoticePreview(v => !v)} className="text-gray-600 hover:underline">
         {noticePreview ? '편집' : '미리보기'}
        </button>
       </div>
      </div>
      {noticePreview ? (
       <div className="w-full min-h-[8rem] px-3 py-2 border bg-gray-50 text-sm text-gray-700 leading-relaxed">
        {renderMarkdown(uploadCfg.uploadPageNotice) ?? <span className="text-gray-400">내용이 없습니다.</span>}
       </div>
      ) : (
       <textarea
        ref={noticeRef}
        value={uploadCfg.uploadPageNotice}
        onChange={e => setUploadCfg({ ...uploadCfg, uploadPageNotice: e.target.value.slice(0, NOTICE_MAX) })}
        placeholder={'업로드 페이지 상단에 표시할 안내(기본 방침 등).\n# 제목, **굵게**, - 목록, [링크](https://…), 이미지(위 "이미지 올리기") 지원'}
        rows={8}
        disabled={uploadCfgSaving}
        className="w-full px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-mono"
       />
      )}
      <p className="text-[11px] text-gray-400 mt-1 text-right">{uploadCfg.uploadPageNotice.length}/{NOTICE_MAX} · 이미지는 5MB 이하. 같은 줄에 여러 장 넣으면 가로로 나란히 배열됩니다. 올린 뒤 "업로드 설정 저장"을 눌러야 반영됩니다</p>
     </div>
     <div className="flex items-center gap-3 pt-2">
      <button
       type="submit"
       disabled={uploadCfgSaving}
       className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm disabled:opacity-50"
      >{uploadCfgSaving ? '저장 중...' : '업로드 설정 저장'}</button>
      {uploadCfgSaved && <span className="text-xs text-green-600">저장됨</span>}
      {uploadCfgError && <span className="text-xs text-red-600">{uploadCfgError}</span>}
     </div>
    </form>
   </section>

   {/* 4. 표시 환경 */}
   <section className="border border-gray-200 p-4">
    <h2 className="text-lg font-semibold mb-1 text-gray-800">표시 환경</h2>
    <p className="text-xs text-gray-500 mb-4">
     날짜/시각·숫자 표시 언어와 시간대. 자동 감지는 브라우저 설정을 따릅니다.
    </p>

    <div className="space-y-3 mb-4">
     <div>
      <label className="block text-xs text-gray-600 mb-1">언어 (locale)</label>
      <select
       value={locale}
       onChange={e => setLocale(e.target.value)}
       disabled={prefSaving}
       className="w-full px-2 py-1 border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
       {LOCALE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
     </div>

     <div>
      <label className="block text-xs text-gray-600 mb-1">시간대 (timezone)</label>
      <select
       value={timezone}
       onChange={e => setTimezone(e.target.value)}
       disabled={prefSaving}
       className="w-full px-2 py-1 border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
       {TIMEZONE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
     </div>
    </div>

    <div className="flex items-center gap-3">
     <button
      onClick={handlePreferencesSave}
      disabled={prefSaving}
      className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm disabled:opacity-50"
     >{prefSaving ? '저장 중...' : '환경 저장'}</button>
     {prefSaved && <span className="text-xs text-green-600">저장됨</span>}
     {prefError && <span className="text-xs text-red-600">{prefError}</span>}
    </div>

    <div className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-500">
     현재 미리보기: <span className="text-gray-700 font-mono">{formatDateTime(new Date())}</span>
    </div>
   </section>

   {/* 관리자 진입 이력 */}
   <section className="mt-8 bg-white border border-gray-200 p-4">
    <h2 className="text-base font-semibold mb-2 flex items-center gap-2">
     <span>🛡️</span>
     <span>관리자 진입 이력</span>
     <a
      href="/policy/admin-access"
      target="_blank"
      rel="noopener"
      className="text-xs text-blue-600 hover:underline ml-2"
     >
      정책 보기
     </a>
    </h2>
    <p className="text-xs text-gray-600 mb-3">
     관리자가 본 계정으로 진입한 이력 (최근 30일). view-as 는 읽기 전용, 운영 위임은 쓰기 가능(계정삭제·메시지 제외)이며 모든 작업이 기록됩니다.
    </p>

    {/* 관리자 접근 차단 스위치 */}
    <div className="mb-4 p-3 bg-gray-50 border border-gray-200 flex items-start justify-between gap-3">
     <div>
      <div className="text-sm font-medium text-gray-800">관리자 접근 차단</div>
      <div className="text-xs text-gray-500 mt-0.5">
       켜면 관리자가 내 계정으로 view-as·운영 위임을 <b>시작할 수 없고</b>, 진행 중인 접근도 즉시 종료됩니다.
      </div>
     </div>
     <button
      onClick={handleToggleAdminAccess}
      disabled={blockSaving}
      className={`flex-shrink-0 px-3 py-1.5 text-xs font-semibold border disabled:opacity-50 ${adminBlocked ? 'bg-red-600 text-white border-red-600 hover:bg-red-700' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'}`}
     >
      {blockSaving ? '...' : adminBlocked ? '차단됨 · 해제하기' : '차단하기'}
     </button>
    </div>

    {/* 우리 매장 스텝 — 내 계정으로 로그인해 함께 운영할 직원의 구글 이메일. 관리자 아닌 사장만. */}
    {!user?.isAdmin && (
     <div className="mb-4 p-3 bg-gray-50 border border-gray-200">
      <div className="text-sm font-medium text-gray-800">우리 매장 스텝</div>
      <div className="text-xs text-gray-500 mt-0.5 mb-2">
       여기에 등록한 <b>구글 이메일</b>로 로그인하면, 그 직원은 내 계정으로 들어와 나와 똑같이
       영상·편성을 관리합니다. (역할 구분 없이 대표 명의로 동작)
      </div>
      <div className="flex gap-2 mb-2">
       <input
        type="email"
        value={staffEmail}
        onChange={(e) => setStaffEmail(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') void addStaff(); }}
        placeholder="staff@example.com"
        className="flex-1 px-2 py-1.5 text-sm border border-gray-300"
       />
       <button
        onClick={addStaff}
        disabled={staffBusy || !staffEmail.trim()}
        className="flex-shrink-0 px-3 py-1.5 text-xs font-semibold border bg-blue-600 text-white border-blue-600 hover:bg-blue-700 disabled:opacity-50"
       >{staffBusy ? '...' : '추가'}</button>
      </div>
      {staffErr && <div className="text-xs text-red-600 mb-2">{staffErr}</div>}
      {staff.length === 0 ? (
       <div className="text-xs text-gray-400">등록된 스텝이 없습니다.</div>
      ) : (
       <ul className="divide-y divide-gray-100 border border-gray-100 bg-white">
        {staff.map((s) => (
         <li key={s.id} className="flex items-center justify-between px-2 py-1.5 text-sm">
          <span className="flex items-center gap-2">
           <span className="text-gray-800">{s.aliasEmail}</span>
           <span className={`text-[10px] px-1.5 py-0.5 ${s.pinned ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
            {s.pinned ? '로그인함' : '대기'}
           </span>
          </span>
          <button
           onClick={() => removeStaff(s.id)}
           className="text-xs text-red-600 hover:underline"
          >해제</button>
         </li>
        ))}
       </ul>
      )}
     </div>
    )}

    {/* 스텝에게 DM 공유 스위치 — 스텝 로그인(내 계정으로 진입한 직원)에게 내 사적 채팅을 보일지. */}
    <div className="mb-4 p-3 bg-gray-50 border border-gray-200 flex items-start justify-between gap-3">
     <div>
      <div className="text-sm font-medium text-gray-800">스텝에게 사적 채팅 공유</div>
      <div className="text-xs text-gray-500 mt-0.5">
       내 계정으로 로그인하는 스텝에게 내 <b>1:1 채팅·쪽지(DM)</b>도 보일지. 끄면 스텝은
       영상·편성은 그대로 운영하되 DM만 볼 수 없습니다. (영상·슬롯 등 나머지는 항상 공유)
      </div>
     </div>
     <button
      onClick={handleToggleShareDirect}
      disabled={shareSaving}
      className={`flex-shrink-0 px-3 py-1.5 text-xs font-semibold border disabled:opacity-50 ${shareDirect ? 'bg-green-600 text-white border-green-600 hover:bg-green-700' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'}`}
     >
      {shareSaving ? '...' : shareDirect ? '공유 중 · 끄기' : '공유 안 함 · 켜기'}
     </button>
    </div>

    {sudoLoading ? (
     <p className="text-sm text-gray-500">로딩 중...</p>
    ) : sudoHistory.length === 0 ? (
     <p className="text-sm text-gray-500">진입 이력 없음</p>
    ) : (
     <table className="w-full text-sm">
      <thead className="bg-gray-50 text-xs text-gray-600">
       <tr className="text-left">
        <th className="px-2 py-1.5">시각</th>
        <th className="px-2 py-1.5">관리자</th>
        <th className="px-2 py-1.5">동작</th>
       </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
       {sudoHistory.map((row, i) => (
        <tr key={i}>
         <td className="px-2 py-1.5 text-xs text-gray-700">{formatDateTime(row.created_at)}</td>
         <td className="px-2 py-1.5 text-xs">{row.actor_name || row.actor_hash.slice(0, 8)}</td>
         <td className="px-2 py-1.5 text-xs">
          {row.action === 'sudo_start' ? (
           <span className="text-amber-700">진입</span>
          ) : (
           <span className="text-green-700">종료</span>
          )}
         </td>
        </tr>
       ))}
      </tbody>
     </table>
    )}
   </section>

   {/* 회원 탈퇴 (Danger Zone) */}
   <section className="mt-8 border border-red-200 bg-red-50 p-4">
    <h2 className="text-base font-semibold mb-1 text-red-800">회원 탈퇴</h2>
    <p className="text-xs text-red-700 mb-3 leading-relaxed">
     탈퇴 시 아래와 같이 처리되며 <b>되돌릴 수 없습니다</b>. 법령상 보존이 필요한 거래·정산 기록은 익명화되어 일정 기간 보관됩니다. (<a href="/terms.html" target="_blank" rel="noopener" className="underline">이용약관 제5조</a>)
    </p>
    <ul className="list-disc list-inside text-xs text-red-700 mb-3 space-y-0.5">
     <li>등록된 스크린(MoClient)의 서비스 연결이 해제됩니다.</li>
     <li>소유한 모패밀리가 해산되고, 업로드한 영상이 삭제됩니다.</li>
     <li>개인정보(이메일·이름·전화·사업자정보 등)가 파기(익명화)됩니다.</li>
     <li>모든 기기에서 즉시 로그아웃됩니다.</li>
    </ul>
    {!withdrawOpen ? (
     <button
      onClick={() => setWithdrawOpen(true)}
      className="px-4 py-2 bg-white text-red-700 border border-red-300 hover:bg-red-100 text-sm"
     >회원 탈퇴</button>
    ) : (
     <div className="space-y-2">
      <label className="block text-xs text-red-800">확인을 위해 <b>탈퇴</b> 를 입력하세요.</label>
      <input
       value={withdrawConfirm}
       onChange={e => setWithdrawConfirm(e.target.value)}
       placeholder="탈퇴"
       disabled={withdrawing}
       className="w-40 px-3 py-2 border border-red-300 text-sm"
      />
      <div className="flex items-center gap-2">
       <button
        onClick={handleWithdraw}
        disabled={withdrawing || withdrawConfirm.trim() !== '탈퇴'}
        className="px-4 py-2 bg-red-600 text-white text-sm hover:bg-red-700 disabled:opacity-50"
       >{withdrawing ? '처리 중...' : '탈퇴 확정'}</button>
       <button
        onClick={() => { setWithdrawOpen(false); setWithdrawConfirm(''); }}
        disabled={withdrawing}
        className="px-3 py-2 text-gray-600 text-sm hover:bg-gray-100"
       >취소</button>
      </div>
     </div>
    )}
   </section>
  </div>
 );
};

export default ProfilePage;
